import { useDispatch, useSelector } from "react-redux";
import { openModal, closeModal, openEditLabModal, closeEditLabModal } from "@store/slices/modalSlice";
import { addLab, updateLab } from "@store/slices/labSlice";
import { useCreateLab, useUpdateLabMutation } from "../service/queries";
import type { Lab } from "../types";
import type { CreateLabRequest, UpdateLabRequest } from "../types";
import type { RootState } from "../../../../../store/store";
import { MODAL_KEYS } from "@constants/global.constants";

export const useLabModal = () => {
	const dispatch = useDispatch();
	const { addLabModal, editLabModal } = useSelector((state: RootState) => state.modal);
	// Lab mutations
	const createLabMutation = useCreateLab();
	const updateLabMutation = useUpdateLabMutation();

	const openAddLabModal = () => {
		dispatch(openModal(MODAL_KEYS.ADD_LAB));
	};

	const closeAddLabModal = () => {
		dispatch(closeModal(MODAL_KEYS.ADD_LAB));
	};

	const openEditLabModalHandler = (lab: Lab) => {
		dispatch(openEditLabModal(lab));
	};

	const closeEditLabModalHandler = () => {
		dispatch(closeEditLabModal());
	};

	// Business logic handlers
	const handleCreateLab = async (labData: CreateLabRequest) => {
		try {
			const response = await createLabMutation.mutateAsync(labData);
			const createdLab = response?.data || response;

			dispatch(addLab(createdLab));
			dispatch(closeModal(MODAL_KEYS.ADD_LAB));
		} catch (error) {
			console.error("Failed to create lab:", error);
		}
	};

	const handleUpdateLab = async (labData: UpdateLabRequest, labUuid: string) => {
		try {
			const response = await updateLabMutation.mutateAsync({
				labUuid,
				labData,
			});

			const updatedLab = response.data || response;
			dispatch(updateLab(updatedLab));
			dispatch(closeEditLabModal());
		} catch (error) {
			// Error notifications are handled in the mutation's onError to ensure
			// a single, consistent global error toast.
			console.error("Failed to update lab:", error);
		}
	};

	return {
		openAddLabModal,
		closeAddLabModal,
		addLabModalOpen: addLabModal.isOpen,
		openEditLabModal: openEditLabModalHandler,
		closeEditLabModal: closeEditLabModalHandler,
		editLabModalOpen: editLabModal.isOpen,
		editLabData: editLabModal.lab,
		handleCreateLab,
		handleUpdateLab,
		isCreatingLab: createLabMutation.isPending,
		isUpdatingLab: updateLabMutation.isPending,
	};
};
